import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Trash2, DollarSign, Edit } from 'lucide-react';

interface FeaturePricing {
  id: string;
  feature_id: string;
  feature_name: string;
  description: string | null;
  base_price_monthly: number;
  base_price_yearly: number | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export default function FeaturePricingManager() {
  const [features, setFeatures] = useState<FeaturePricing[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingFeature, setEditingFeature] = useState<FeaturePricing | null>(null);

  // Form state
  const [featureId, setFeatureId] = useState('');
  const [featureName, setFeatureName] = useState('');
  const [description, setDescription] = useState('');
  const [priceMonthly, setPriceMonthly] = useState('');
  const [priceYearly, setPriceYearly] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [sortOrder, setSortOrder] = useState(0);

  useEffect(() => {
    loadFeatures();
  }, []);

  const loadFeatures = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('feature_pricing')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setFeatures(data || []);
    } catch (error) {
      console.error('Error loading features:', error);
      toast.error('Failed to load features');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFeatureId('');
    setFeatureName('');
    setDescription('');
    setPriceMonthly('');
    setPriceYearly('');
    setIsActive(true);
    setSortOrder(0);
    setEditingFeature(null);
  };

  const handleEdit = (feature: FeaturePricing) => {
    setEditingFeature(feature);
    setFeatureId(feature.feature_id);
    setFeatureName(feature.feature_name);
    setDescription(feature.description || '');
    setPriceMonthly(feature.base_price_monthly.toString());
    setPriceYearly(feature.base_price_yearly?.toString() || '');
    setIsActive(feature.is_active);
    setSortOrder(feature.sort_order);
    setIsDialogOpen(true);
  };

  const handleSaveFeature = async () => {
    if (!featureId || !featureName || !priceMonthly) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const featureData = {
        feature_id: featureId,
        feature_name: featureName,
        description: description || null,
        base_price_monthly: parseFloat(priceMonthly),
        base_price_yearly: priceYearly ? parseFloat(priceYearly) : null,
        is_active: isActive,
        sort_order: sortOrder,
      };

      if (editingFeature) {
        const { error } = await supabase
          .from('feature_pricing')
          .update(featureData)
          .eq('id', editingFeature.id);

        if (error) throw error;
        toast.success('Feature updated successfully');
      } else {
        const { error } = await supabase
          .from('feature_pricing')
          .insert([featureData]);

        if (error) throw error;
        toast.success('Feature created successfully');
      }

      setIsDialogOpen(false);
      resetForm();
      loadFeatures();
    } catch (error) {
      console.error('Error saving feature:', error);
      toast.error('Failed to save feature');
    }
  };

  const handleDeleteFeature = async (id: string) => {
    if (!confirm('Are you sure you want to delete this feature?')) return;

    try {
      const { error } = await supabase
        .from('feature_pricing')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Feature deleted successfully');
      loadFeatures();
    } catch (error) {
      console.error('Error deleting feature:', error);
      toast.error('Failed to delete feature');
    }
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('feature_pricing')
        .update({ is_active: !currentStatus })
        .eq('id', id);

      if (error) throw error;
      toast.success(`Feature ${!currentStatus ? 'activated' : 'deactivated'}`);
      loadFeatures();
    } catch (error) {
      console.error('Error toggling feature status:', error);
      toast.error('Failed to update feature status');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <DollarSign className="h-6 w-6" />
            Feature Pricing
          </h2>
          <p className="text-muted-foreground mt-1">
            Manage individual feature pricing and availability
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Feature
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingFeature ? 'Edit Feature' : 'Create New Feature'}</DialogTitle>
              <DialogDescription>
                Set pricing for individual app features
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="feature-id">Feature ID *</Label>
                  <Input
                    id="feature-id"
                    value={featureId}
                    onChange={(e) => setFeatureId(e.target.value)}
                    placeholder="sanctuary"
                    disabled={!!editingFeature}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="feature-name">Feature Name *</Label>
                  <Input
                    id="feature-name"
                    value={featureName}
                    onChange={(e) => setFeatureName(e.target.value)}
                    placeholder="The Sanctuary"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Access to condition-specific reflection tools"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price-monthly">Monthly Price (£) *</Label>
                  <Input
                    id="price-monthly"
                    type="number"
                    step="0.01"
                    value={priceMonthly}
                    onChange={(e) => setPriceMonthly(e.target.value)}
                    placeholder="29.99"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price-yearly">Yearly Price (£)</Label>
                  <Input
                    id="price-yearly"
                    type="number"
                    step="0.01"
                    value={priceYearly}
                    onChange={(e) => setPriceYearly(e.target.value)}
                    placeholder="299.99"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sort-order">Sort Order</Label>
                  <Input
                    id="sort-order"
                    type="number"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
                  />
                </div>
                <div className="flex items-center space-x-2 pt-8">
                  <Switch
                    id="is-active"
                    checked={isActive}
                    onCheckedChange={setIsActive}
                  />
                  <Label htmlFor="is-active">Active</Label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveFeature}>
                {editingFeature ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Feature</TableHead>
              <TableHead>Monthly</TableHead>
              <TableHead>Yearly</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {features.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  No features found
                </TableCell>
              </TableRow>
            ) : (
              features.map((feature) => (
                <TableRow key={feature.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{feature.feature_name}</div>
                      <div className="text-sm text-muted-foreground">{feature.feature_id}</div>
                      {feature.description && (
                        <div className="text-sm text-muted-foreground mt-1">{feature.description}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>£{feature.base_price_monthly.toFixed(2)}</TableCell>
                  <TableCell>
                    {feature.base_price_yearly ? `£${feature.base_price_yearly.toFixed(2)}` : '-'}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={feature.is_active}
                      onCheckedChange={() => handleToggleActive(feature.id, feature.is_active)}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(feature)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteFeature(feature.id)}
                        className="hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
